import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../../translations/en.json';
import esTranslations from '../../translations/es.json';
import { useAuth } from '../auth/AuthContext';
import { getUserProfile, updateUserLanguage } from '../../services/userService';

export const LanguageContext = createContext();

// Translation files mapped by language code
const translationsMap = {
  en: enTranslations,
  es: esTranslations
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [language, setLanguage] = useState('en');
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(true);

  // Get initial language with hierarchy:
  // 1. Firestore user profile (if logged in)
  // 2. Stored selection (localStorage)
  // 3. Browser's default language (if Spanish, use Spanish; otherwise English)
  useEffect(() => {
    const loadLanguage = async () => {
      setIsLoadingLanguage(true);
      
      try {
        if (currentUser) {
          // User is logged in - try to load from Firestore
          const userProfile = await getUserProfile(currentUser.uid);
          if (userProfile && userProfile.language && (userProfile.language === 'en' || userProfile.language === 'es')) {
            setLanguage(userProfile.language);
            // Also sync to localStorage for consistency
            localStorage.setItem('preferredLanguage', userProfile.language);
            setIsLoadingLanguage(false);
            return;
          }
        }
        
        // Fallback to localStorage
        const savedLanguage = localStorage.getItem('preferredLanguage');
        if (savedLanguage === 'en' || savedLanguage === 'es') {
          setLanguage(savedLanguage);
          setIsLoadingLanguage(false);
          return;
        }
        
        // Fallback to browser language
        const browserLang = navigator.language || navigator.userLanguage;
        const browserLangCode = browserLang.split('-')[0].toLowerCase();
        
        // If browser is set to Spanish, use Spanish; otherwise default to English
        const defaultLanguage = browserLangCode === 'es' ? 'es' : 'en';
        setLanguage(defaultLanguage);
        localStorage.setItem('preferredLanguage', defaultLanguage);
      } catch (error) {
        console.error('Error loading language preference:', error);
        // Fallback to localStorage on error
        const savedLanguage = localStorage.getItem('preferredLanguage');
        if (savedLanguage === 'en' || savedLanguage === 'es') {
          setLanguage(savedLanguage);
        } else {
          setLanguage('en');
        }
      } finally {
        setIsLoadingLanguage(false);
      }
    };

    loadLanguage();
  }, [currentUser]);

  // Get translations for current language, fallback to English if not available
  const translations = translationsMap[language] || translationsMap.en;

  // Save language preference to Firestore (if logged in) and localStorage whenever it changes
  useEffect(() => {
    if (isLoadingLanguage) return; // Don't save during initial load
    
    // Always save to localStorage for consistency
    localStorage.setItem('preferredLanguage', language);
    
    // Invalidate language cache in service files so error messages use new language immediately
    // This ensures translations are synchronized across the entire app
    try {
      // Dynamic import to avoid circular dependencies
      import('../../services/mentorClientService').then(module => {
        if (module.invalidateLanguageCache) {
          module.invalidateLanguageCache();
        }
      });
    } catch (error) {
      // Ignore errors - cache will expire naturally
      console.debug('Could not invalidate language cache:', error);
    }
    
    // Save to Firestore if user is logged in
    if (currentUser) {
      updateUserLanguage(currentUser.uid, language).catch(error => {
        console.error('Error saving language to Firestore:', error);
        // Don't block the language change if Firestore save fails
      });
    }
  }, [language, currentUser, isLoadingLanguage]);

  const changeLanguage = (lang) => {
    if (lang === 'en' || lang === 'es') {
      setLanguage(lang);
    }
  };

  const value = {
    language,
    changeLanguage,
    isEnglish: language === 'en',
    isSpanish: language === 'es',
    translations,
    isLoadingLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

